import store, { getStoreState } from 'config/store'
import { getAttributeFromCampaignOrDefault, getCampaignFromEmbedById } from 'modules/campaigns/utils'
import { getActiveConversationId } from 'modules/conversations/selectors'
import { getOrgId } from 'modules/embed/utils'
import { setHasSentImpression } from 'modules/metrics/actions'
import { isNil, pathOr } from 'ramda'
import { CampaignReenrollmentType } from 'types/campaigns'
import { WidgetImpressionRequest, WidgetImpressionSource } from 'types/targeting'
import { error } from 'utils'

import { hasSentImpression } from './selectors'
import { getImpressionSendId, isPlaybookImpressionEvent } from './utils'


const getTimeToImpression = (initTimestamp: number): number | undefined => {
  if (!initTimestamp || hasSentImpression()) {
    return undefined
  }

  store.dispatch(setHasSentImpression())
  return Date.now() - initTimestamp
}

export const createImpressionEvent = async ({
  source,
  meta = {}
}: {
  source: WidgetImpressionSource,
  meta: { playbookId?: number, virtual?: boolean, interactionId?: number, conversationId?: number, isDelayed?: boolean, isExperiment?: boolean, playbookVersion?: number }
}): Promise<WidgetImpressionRequest | null> => {
  const state = getStoreState()
  const { session, campaigns = {}, metrics = {} } = state
  try {
    const orgId = getOrgId()
    const endUserId = pathOr(null, ['endUser', 'id'], session)
    const activeCampaignId = pathOr(null, ['activeCampaign', 'id'], campaigns)
    const activeConversationId = meta.conversationId || getActiveConversationId(state)
    const sessionId = session?.id
    const instanceId = session?.instanceId
    const sentAt = Date.now()
    const url = pathOr('', ['context', 'page', 'url'], session)
    const endUserCountry = pathOr('', ['geo', 'country'], session)
    const userAgent: string = pathOr('', ['context', 'userAgent'], session)
    const playbookId = !isNil(meta.playbookId) ? meta.playbookId : undefined
    const interactionId = !isNil(meta.interactionId) ? meta.interactionId : undefined
    const virtual = meta.virtual || false
    const isDelayed = meta.isDelayed || false
    // only include isExperiment if it's true
    const isExperiment = meta.isExperiment ? { isExperiment: meta.isExperiment } : {}
    const playbookVersion = !isNil(meta.playbookVersion) ? meta.playbookVersion : undefined

    let sendId
    if (isPlaybookImpressionEvent(source)) {
      const impressionCampaign = getCampaignFromEmbedById(activeCampaignId)
      const impressionCampaignReenrollmentType: CampaignReenrollmentType = getAttributeFromCampaignOrDefault(impressionCampaign, ['campaignReenrollmentType'], null)
      sendId = await getImpressionSendId(playbookId, endUserId, sessionId, instanceId, activeConversationId, impressionCampaignReenrollmentType, virtual)
    }

    const timeToImpression = getTimeToImpression(metrics.initTimestamp)

    return {
      orgId,
      endUserId,
      sessionId,
      sentAt,
      impressionSource: {
        source,
        playbookId,
        interactionId,
        instanceId: instanceId?.toString(),
        virtual,
        widgetVersion: 2,
        sendId,
        timeToImpression,
        delayed: isDelayed,
        ...isExperiment,
        playbookVersion
      },
      context: {
        url,
        userAgent,
        country: endUserCountry
      }
    }
  } catch (err) {
    error(new Error(`Failed to create impression event: ${err.message}`))
    return null
  }
}
